import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../ui/DataGrid';
import DataCard from '../ui/DataCard';
import QuizStudentAccessSettings, { 
  DEFAULT_STUDENT_ACCESS, 
  VISIBILITY_OPTIONS 
} from './QuizStudentAccessSettings';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';

/**
 * CourseTemplatesTab - Vorlagen Tab in der CourseView
 * 
 * Zeigt alle Vorlagen eines Kurses als Grid mit:
 * - Template-Info (Name, Fragen-Anzahl)
 * - Tags
 * - Studierenden-Zugang (Sichtbarkeit, Übungsmodus)
 * - Aktionen (Quiz starten, Bearbeiten, Löschen)
 */
export default function CourseTemplatesTab({
  templates,
  onStartQuiz,
  onEdit,
  onDuplicate,
  onDelete,
  onCreate,
  onTemplateUpdate
}) {
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();
  const [expandedSettings, setExpandedSettings] = useState(null);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Nie';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) return 'Heute';
    if (diff < 172800000) return 'Gestern';
    if (diff < 604800000) return `vor ${Math.floor(diff / 86400000)} Tagen`;
    
    return date.toLocaleDateString('de-DE');
  };

  // Get visibility badge info
  const getVisibilityBadge = (studentAccess) => {
    const settings = studentAccess || DEFAULT_STUDENT_ACCESS;
    const option = VISIBILITY_OPTIONS.find(o => o.value === settings.visibility);
    
    return {
      label: option?.label || 'Sichtbar',
      variant: settings.visibility === 'hidden' ? 'warning' : 
               settings.visibility === 'after_completion' ? 'info' : 'default'
    };
  };

  // Get practice badge with icon  
  const getPracticeBadge = (studentAccess) => {
    const settings = studentAccess || DEFAULT_STUDENT_ACCESS;
    if (!settings.allowPractice) return null;
    
    // Using a simple icon representation that works with Badge component
    return {
      label: settings.practiceAfterSession ? '↻ Üben (nach Session)' : '↻ Üben',
      variant: 'success'
    };
  };

  return (
    <div className="space-y-4">
      <DataGrid
        items={templates}
        columns={{ sm: 1, md: 2, lg: 2 }}
        gap="md"
        addCard={{
          label: 'Neue Vorlage erstellen',
          onClick: onCreate
        }}
        emptyState={{
          title: 'Noch keine Vorlagen',
          description: 'Erstelle deine erste Quizvorlage für diesen Kurs',
          actions: [
            { label: 'Vorlage erstellen', onClick: onCreate },
            { label: 'Mit KI generieren', onClick: () => navigate('/llm-generator'), variant: 'secondary' }
          ]
        }}
        renderItem={(template) => {
          const visibilityBadge = getVisibilityBadge(template.studentAccess);
          const practiceBadge = getPracticeBadge(template.studentAccess);
          const allBadges = [
            visibilityBadge,
            practiceBadge,
            ...(template.tags?.slice(0, 2).map(tag => ({ label: tag })) || [])
          ].filter(Boolean);

          return (
            <div key={template.id} className="relative">
              <DataCard
                title={template.name}
                subtitle={template.description}
                stats={[
                  { value: template.questions?.length || 0, label: 'Fragen' },
                  { value: template.useCount || 0, label: 'Verwendet' }
                ]}
                metadata={`Zuletzt bearbeitet: ${formatDate(template.lastModified || template.createdAt)}`}
                badges={allBadges}
                primaryAction={{
                  label: 'Quiz starten',
                  onClick: () => onStartQuiz(template.id),
                  disabled: !template.questions?.length
                }}
                menuActions={[
                  { label: 'Bearbeiten', onClick: () => onEdit(template.id) },
                  { 
                    label: 'Studierenden-Zugang', 
                    onClick: () => setExpandedSettings(
                      expandedSettings === template.id ? null : template.id
                    )
                  },
                  { label: 'Duplizieren', onClick: () => onDuplicate(template.id) },
                  { label: 'Löschen', onClick: () => onDelete(template), variant: 'danger' }
                ]}
                onClick={() => onEdit(template.id)}
              />
              
              {/* Expanded Student Access Settings */}
              {expandedSettings === template.id && (
                <div className="mt-2 animate-slideDown">
                  <QuizStudentAccessSettings
                    userId={user?.uid}
                    templateId={template.id}
                    currentSettings={template.studentAccess}
                    onUpdate={(newSettings) => {
                      if (onTemplateUpdate) {
                        onTemplateUpdate(template.id, { studentAccess: newSettings });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}

CourseTemplatesTab.propTypes = {
  templates: PropTypes.array.isRequired,
  onStartQuiz: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onTemplateUpdate: PropTypes.func
};
